import {useCallback} from 'react';
import {useMutation} from '@apollo/client';
import {CountryPanelFormData} from '../components/CountryPanel/CountryPanel.types';
import createCountryMutation from '../graphql/createCountryMutation';
import updateCountryMutation from '../graphql/updateCountryMutation';
import {Country} from '../store/types';

interface UseSaveCountryResult {
  saveCountry: (
    country: CountryPanelFormData,
    onSuccess: (country: Country) => void,
    onFailure: (error: string) => void
  ) => void;
}

export const useSaveCountry = (): UseSaveCountryResult => {
  const [createCountry] = useMutation(createCountryMutation, {
    errorPolicy: 'all'
  });

  const [updateCountry] = useMutation(updateCountryMutation, {
    errorPolicy: 'all'
  });

  const saveCountry = useCallback(
    async (
      formData: CountryPanelFormData,
      onSuccess: (country: Country) => void,
      onFailure: (error: string) => void
    ) => {
      const country = {
        id: formData.id,
        name: formData.name,
        businessRegion: formData.businessRegion,
        currency: formData.currency,
        repairCostDimension: formData.repairCostDimension,
        maintenanceCostDimension: formData.maintenanceCostDimension,
        countryCodes: formData.countryCodes
      } as Country;

      try {
        const result = formData.id
          ? await updateCountry({variables: country})
          : await createCountry({variables: country});

        if (result.errors && result.errors.length > 0) {
          const errorMessage = result.errors[0].message;
          onFailure(errorMessage);
          return;
        }

        onSuccess(
          formData.id ? result.data.updateCountry : result.data.createCountry
        );
      } catch (err: any) {
        onFailure(`Unexpected error while saving country: ${err}`);
      }
    },
    [createCountry, updateCountry]
  );

  return {saveCountry};
};
